import { useState, type ReactNode } from 'react'
import { Button, TextField } from '@krp/ui-kit'
import { codeProblem, profileProblem, type OwnCashierProfileInput } from '../model/profile'

export interface OwnCashierProfileFormProps {
  /** Начальные значения; форма монтируется заново (`key`) при смене кассира. */
  initial: OwnCashierProfileInput
  /** Новый кассир: в форме ещё и код (одним полем, необязательно) — пусто означает код по умолчанию «1111». */
  withCode: boolean
  codeMinLength: number
  codeMaxLength: number
  canManage: boolean
  busy: boolean
  submitLabel: string
  submitActionId: string
  submit: (input: OwnCashierProfileInput, code: string | null) => Promise<boolean>
  cancel?: () => void
  /** Кнопка справа в верхней строке карточки (например, «Пометить на удаление»): «Сохранить» слева, эта — справа. */
  headerRight?: ReactNode
}

/** ФИО и должность кассира; у нового кассира — ещё и код одним полем. Код после отправки стирается из поля. */
export function OwnCashierProfileForm(props: OwnCashierProfileFormProps) {
  const [input, setInput] = useState(props.initial)
  const [code, setCode] = useState('')
  const [touched, setTouched] = useState(false)
  const patch = (value: Partial<OwnCashierProfileInput>) => { setInput((current) => ({ ...current, ...value })); setTouched(true) }
  // Код при создании необязателен: пусто — кассир получит код по умолчанию «1111». Непустой проверяем длиной и запретом пробелов.
  const problem = profileProblem(input) ?? (props.withCode && code !== '' ? codeProblem(code, props.codeMinLength, props.codeMaxLength) : null)
  const ready = props.canManage && !props.busy && problem === null
  const send = async () => {
    if (!ready) return
    const done = await props.submit(input, props.withCode ? code : null)
    setCode('')
    if (done) setTouched(false)
  }
  return <fieldset className="own-cashiers__form" disabled={!props.canManage || props.busy}>
    <div className="own-cashier-card__header">
      <div className="own-cashier-card__actions">
        <Button variant="primary" actionId={props.submitActionId} disabled={!ready} disabledReason={props.canManage ? problem : 'Нужно право управления кассирами'} busy={props.busy}
          onClick={() => { void send() }}>{props.submitLabel}</Button>
        {props.cancel !== undefined && <Button actionId="receipts.own-cashiers.choose" disabled={props.busy} onClick={props.cancel}>Отмена</Button>}
      </div>
      {props.headerRight}
    </div>
    <TextField label="Фамилия" value={input.lastName} onChange={(value) => patch({ lastName: value })} />
    <TextField label="Имя" value={input.firstName} onChange={(value) => patch({ firstName: value })} />
    <TextField label="Отчество (необязательно)" value={input.middleName} onChange={(value) => patch({ middleName: value })} />
    <TextField label="Должность (необязательно)" value={input.position} onChange={(value) => patch({ position: value })} />
    {props.withCode && <TextField label={`Код кассира (от ${props.codeMinLength} знаков: PIN, буквы-цифры, токен или штрихкод; пусто — будет 1111)`} type="password"
      value={code} onChange={(value) => { setCode(value); setTouched(true) }} />}
    {touched && problem !== null && <p role="status">⚠ {problem}</p>}
  </fieldset>
}
